// Curated canvas backgrounds for workspace branding. The old engine derived the
// page background from the brand's COMPLEMENT hue at fairly high saturation — a
// blue brand gave a cream page, red gave pale ice, etc. It read as loud and
// arbitrary. These presets are deliberately UNDERSTATED: near-neutral paper at
// very low saturation, low contrast, tasteful in both light and dark. The
// default ('auto') simply whispers the brand's own hue into the bone.

import { hslToHex } from './palette'

type Triplet = [h: number, s: number, l: number]
export interface BgResolved {
  bg: Triplet
  elev: Triplet
}
export interface BgPreset {
  key: string
  label: string
  /** A visible swatch (a soft pastel of the hue) for the picker. */
  swatch: string
  light: BgResolved
  dark: BgResolved
}

/** Build a low-saturation paper canvas from one hue. */
function paper(h: number, sLight: number, sDark: number): { light: BgResolved; dark: BgResolved } {
  return {
    light: { bg: [h, sLight, 95.6], elev: [h, Math.min(sLight + 5, 44), 99] },
    dark: { bg: [h, sDark, 7.6], elev: [h, sDark, 10.8] },
  }
}

interface PresetSeed {
  key: string
  label: string
  h: number
  sLight: number
  sDark: number
}

// Default ('auto') is handled in theme.ts straight off the brand hue; these are
// the explicit alternatives shown in the picker. Ordered by hue so the dot
// picker reads as a rainbow sweep, matching the primary color scheme grid.
const SEEDS: PresetSeed[] = [
  { key: 'porcelain', label: 'Porcelain', h: 220, sLight: 0, sDark: 0 },
  { key: 'blush', label: 'Blush', h: 6, sLight: 15, sDark: 12 },
  { key: 'peach', label: 'Peach', h: 22, sLight: 20, sDark: 12 },
  { key: 'sand', label: 'Sand', h: 32, sLight: 24, sDark: 13 },
  { key: 'bone', label: 'Bone', h: 38, sLight: 14, sDark: 10 },
  { key: 'linen', label: 'Linen', h: 52, sLight: 18, sDark: 11 },
  { key: 'olive', label: 'Olive', h: 78, sLight: 12, sDark: 9 },
  { key: 'sage', label: 'Sage', h: 140, sLight: 12, sDark: 10 },
  { key: 'mint', label: 'Mint', h: 162, sLight: 14, sDark: 10 },
  { key: 'sea', label: 'Sea', h: 186, sLight: 14, sDark: 11 },
  { key: 'ice', label: 'Ice', h: 198, sLight: 18, sDark: 12 },
  { key: 'cloud', label: 'Cloud', h: 212, sLight: 13, sDark: 12 },
  { key: 'graphite', label: 'Graphite', h: 220, sLight: 4, sDark: 6 },
  { key: 'indigo', label: 'Indigo', h: 240, sLight: 10, sDark: 11 },
  { key: 'lilac', label: 'Lilac', h: 266, sLight: 14, sDark: 12 },
  { key: 'mauve', label: 'Mauve', h: 290, sLight: 11, sDark: 10 },
  { key: 'rose', label: 'Rose', h: 344, sLight: 14, sDark: 11 },
]

export const BG_PRESETS: BgPreset[] = SEEDS.map((s) => ({
  key: s.key,
  label: s.label,
  swatch: hslToHex(s.h, Math.min(s.sLight * 2.4, 42), 82),
  ...paper(s.h, s.sLight, s.sDark),
}))

/** The understated default canvas, following the brand's own hue. */
export function autoBackground(brandHue: number): { light: BgResolved; dark: BgResolved } {
  return {
    light: { bg: [brandHue, 8, 95.6], elev: [brandHue, 10, 99] },
    dark: { bg: [brandHue, 9, 7.6], elev: [brandHue, 9, 10.8] },
  }
}

export const DEFAULT_BG_KEY = 'auto'

/** Resolve a stored key to a preset, or null when it's the auto default. */
export function findBackground(key?: string | null): BgPreset | null {
  if (!key || key === DEFAULT_BG_KEY) return null
  return BG_PRESETS.find((p) => p.key === key) ?? null
}
